export type FeedbackSentiment = 'up' | 'down';
export type FeedbackData = {
	sentiment: FeedbackSentiment;
	issueType: string;
	details: string;
};
export const FEEDBACK_ISSUE_OPTIONS = [
	{ value: 'inaccurate', label: 'Неточно' },
	{ value: 'unhelpful', label: 'Не помог' },
	{ value: 'unclear', label: 'Непонятно' },
	{ value: 'harmful', label: 'Грубо или некорректно' },
	{ value: 'other', label: 'Другое' },
];
export const AI_FEEDBACK_COMMENT_LIMIT = 3900;
export type AIFeedbackContext = { aiDialogId: string; aiMessageId: number };
export type AIFeedbackTarget = Readonly<
	AIFeedbackContext & { aiProblemId: string; sentiment: FeedbackSentiment }
>;
export function isPersistedFeedbackMessage(
	messageId: unknown,
): messageId is number {
	return (
		typeof messageId === 'number' &&
		Number.isInteger(messageId) &&
		messageId > 0 &&
		messageId <= 2147483647
	);
}
function validContext(dialogId: unknown, messageId: unknown): boolean {
	return (
		typeof dialogId === 'string' &&
		/^[0-9a-fA-F]{32}$/.test(dialogId) &&
		isPersistedFeedbackMessage(messageId)
	);
}
export function captureAIFeedbackTarget(
	aiProblemId: string,
	aiDialogId: string,
	aiMessageId: unknown,
	sentiment: FeedbackSentiment,
): AIFeedbackTarget | null {
	if (!validContext(aiDialogId, aiMessageId)) return null;
	return Object.freeze({
		aiProblemId,
		aiDialogId: aiDialogId.toUpperCase(),
		aiMessageId: aiMessageId as number,
		sentiment,
	});
}
export type AIFeedbackInput = AIFeedbackContext & {
	aiProblemId: string;
	title: string;
	message: string;
};
export type AIFeedbackError =
	| 'invalid_input'
	| 'unauthorized'
	| 'forbidden'
	| 'not_found'
	| 'save_failed';
export type AIFeedbackResult =
	| { success: true }
	| { success: false; error: AIFeedbackError };
export type AIFeedbackFlow = 'practice' | 'interview' | 'candidate';

export function buildAIFeedback(
	aiProblemId: string,
	problemTitle: string,
	data: FeedbackData,
	context: AIFeedbackContext,
): AIFeedbackInput {
	const negative = data.sentiment === 'down';
	const prefix = negative ? 'Ошибка в AI-задаче:' : 'Отзыв об AI-задаче:';
	const reason = negative
		? FEEDBACK_ISSUE_OPTIONS.find((option) => option.value === data.issueType)
				?.label
		: undefined;
	return {
		aiProblemId: aiProblemId.trim(),
		aiDialogId: context.aiDialogId.toUpperCase(),
		aiMessageId: context.aiMessageId,
		title: Array.from(prefix + problemTitle)
			.slice(0, 128)
			.join(''),
		message: `Оценка:${negative ? 'Не понравилось' : 'Понравилось'}\nВыбраны опции:${reason ?? 'Не выбрано'}\nСообщение пользователя:${data.details.trim()}`,
	};
}
export function validateAIFeedback(input: AIFeedbackInput): boolean {
	return (
		!!input &&
		validContext(input.aiDialogId, input.aiMessageId) &&
		[
			['aiProblemId', 50],
			['title', 128],
			['message', 4096],
		].every(([key, max]) => {
			const value = input[key as keyof AIFeedbackInput];
			return (
				typeof value === 'string' &&
				value.trim().length > 0 &&
				Array.from(key === 'aiProblemId' ? value.trim() : value).length <=
					Number(max)
			);
		})
	);
}
type PostAIFeedback = (
	id: string,
	body: {
		title: string;
		message: string;
		ai_dialog_id: string;
		ai_message_id: number;
	},
	params: { headers: { cookie: string } },
) => Promise<{ status: number; error?: unknown }>;
export async function saveAIFeedback(
	input: AIFeedbackInput,
	cookie: string,
	post: PostAIFeedback,
): Promise<AIFeedbackResult> {
	if (!validateAIFeedback(input))
		return { success: false, error: 'invalid_input' };
	try {
		const response = await post(
			encodeURIComponent(input.aiProblemId.trim()),
			{
				title: input.title,
				message: input.message,
				ai_dialog_id: input.aiDialogId.toUpperCase(),
				ai_message_id: input.aiMessageId,
			},
			{ headers: { cookie } },
		);
		if (response.status === 201 && !response.error) return { success: true };
		const errors: Record<number, AIFeedbackError> = {
			400: 'invalid_input',
			401: 'unauthorized',
			403: 'forbidden',
			404: 'not_found',
		};
		return { success: false, error: errors[response.status] ?? 'save_failed' };
	} catch {
		return { success: false, error: 'save_failed' };
	}
}

type Goal =
	| 'OPEN'
	| 'CHOICE'
	| 'TEXT_STARTED'
	| 'TEXT_COMPLETED'
	| 'SUBMIT'
	| 'RETRY'
	| 'SUCCESS'
	| 'ERROR'
	| 'CLOSE';
type Parameters = {
	option_id?: string;
	has_text?: boolean;
	error_kind?: AIFeedbackError;
	source?: 'cancel' | 'dismiss';
	stage?: 'validation' | 'save';
};
export class AIFeedbackAnalytics {
	private send: (goal: string, parameters: Record<string, unknown>) => void;
	private flow: AIFeedbackFlow;
	private sentiment: FeedbackSentiment;
	private opened = false;
	private started = false;
	private completed = false;
	constructor(
		send: (goal: string, parameters: Record<string, unknown>) => void,
		flow: AIFeedbackFlow,
		sentiment: FeedbackSentiment,
	) {
		this.send = send;
		this.flow = flow;
		this.sentiment = sentiment;
	}
	track(goal: Goal, parameters: Parameters = {}) {
		const safe: Record<string, unknown> = {
			entry_point: 'answer_rating',
			flow: this.flow,
			sentiment: this.sentiment,
		};
		if (
			parameters.option_id === '' ||
			FEEDBACK_ISSUE_OPTIONS.some(
				(option) => option.value === parameters.option_id,
			)
		)
			safe.option_id = parameters.option_id || 'none';
		for (const key of ['has_text', 'error_kind', 'source', 'stage'] as const)
			if (parameters[key] !== undefined) safe[key] = parameters[key];
		try {
			this.send(`AI_FEEDBACK_${goal}`, { ai_feedback: safe });
		} catch {
			/* Analytics cannot interrupt feedback. */
		}
	}
	open() {
		if (!this.opened) {
			this.opened = true;
			this.track('OPEN');
		}
	}
	textStarted() {
		if (!this.started) {
			this.started = true;
			this.track('TEXT_STARTED');
		}
	}
	textCompleted(hasText: boolean) {
		if (!this.completed && this.started) {
			this.completed = true;
			this.track('TEXT_COMPLETED', { has_text: hasText });
		}
	}
}

export class AIFeedbackSubmission {
	private pending = false;
	private active = true;
	setActive(active: boolean) {
		this.active = active;
	}
	private failed = false;
	private analytics: AIFeedbackAnalytics;
	constructor(analytics: AIFeedbackAnalytics) {
		this.analytics = analytics;
	}
	canClose() {
		return !this.pending;
	}
	async submit(
		input: AIFeedbackInput,
		save: (input: AIFeedbackInput) => Promise<AIFeedbackResult>,
		callbacks: {
			pending: (value: boolean) => void;
			error: (value: AIFeedbackError | null) => void;
			success: () => void;
		},
	): Promise<boolean> {
		if (this.pending || !this.active) return false;
		if (!validateAIFeedback(input)) {
			callbacks.error('invalid_input');
			this.analytics.track('ERROR', {
				error_kind: 'invalid_input',
				stage: 'validation',
			});
			return false;
		}
		this.pending = true;
		callbacks.pending(true);
		callbacks.error(null);
		if (this.failed) this.analytics.track('RETRY', { stage: 'save' });
		this.analytics.track('SUBMIT');
		try {
			let result: AIFeedbackResult;
			try {
				result = await save(input);
			} catch {
				result = { success: false, error: 'save_failed' };
			}
			if (!this.active) return false;
			if (!result.success) {
				this.failed = true;
				callbacks.error(result.error);
				this.analytics.track('ERROR', {
					error_kind: result.error,
					stage: 'save',
				});
				return false;
			}
			this.analytics.track('SUCCESS');
			callbacks.success();
			return true;
		} finally {
			this.pending = false;
			if (this.active) callbacks.pending(false);
		}
	}
}
